import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Package, Mail, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import productPackage from "@/assets/product-package.jpeg";

export const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  
  // Demo order data
  const orderNumber = "LUN-" + Math.random().toString(36).substr(2, 9).toUpperCase();
  const orderDate = new Date().toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
  
  const orderItems = [
    {
      id: 1,
      name: "Women's Balance Blend",
      image: productPackage,
      size: "25g",
      price: 11.11,
      quantity: 1,
    },
  ];

  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 50 ? 0 : 6.95;
  const total = subtotal + shipping;

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-emerald-50/30 pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100 mb-6 animate-scale-in">
                <CheckCircle className="w-16 h-16 text-emerald-600" />
              </div>
              
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-gold mb-4 text-glow">
                Bedankt voor je bestelling!
              </h1>
              
              <p className="text-lg text-muted-foreground mb-2">
                Je bestelling is succesvol geplaatst
              </p>
              
              <p className="text-sm text-muted-foreground">
                Ordernummer: <span className="font-semibold text-foreground">{orderNumber}</span>
              </p>
            </div>

            {/* Confirmation Message */}
            <div className="bg-card rounded-2xl border-2 border-gold/20 p-8 shadow-lg mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg text-foreground mb-2">
                    Bevestigingsmail verzonden
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    We hebben een bevestigingsmail gestuurd met alle orderdetails. 
                    Controleer ook je spam folder als je de mail niet ziet.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Package className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg text-foreground mb-2">
                    Geschatte levertijd
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Je bestelling wordt binnen 2-3 werkdagen bezorgd. 
                    Je ontvangt een track & trace code zodra je pakket verzonden is.
                  </p>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-card rounded-2xl border-2 border-gold/20 p-8 shadow-lg mb-8">
              <h2 className="font-serif text-2xl font-bold text-gold mb-6">
                Order Details
              </h2>

              {/* Order Items */}
              <div className="space-y-4 mb-6 pb-6 border-b border-muted">
                {orderItems.map((item) => (
                  <div key={item.id} className="flex gap-4 items-center">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{item.name}</h3>
                      <p className="text-sm text-muted-foreground">{item.size}</p>
                      <p className="text-sm text-muted-foreground">Aantal: {item.quantity}</p>
                    </div>
                    <p className="text-gold font-bold">€{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="space-y-2">
                <div className="flex justify-between text-foreground">
                  <span>Subtotaal</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-foreground">
                  <span>Verzendkosten</span>
                  <span>{shipping === 0 ? "Gratis" : `€${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-gold pt-4 border-t border-muted">
                  <span>Totaal</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Order Info */}
              <div className="mt-6 pt-6 border-t border-muted text-sm text-muted-foreground">
                <p>Besteldatum: {orderDate}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate("/")}
                className="btn-hero flex-1"
              >
                Terug naar Home
              </Button>
              
              <Button
                onClick={() => navigate("/producten")}
                variant="outline"
                className="flex-1 border-gold text-gold hover:bg-gold/10"
              >
                Verder Winkelen
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Support Message */}
            <div className="mt-8 text-center">
              <p className="text-sm text-muted-foreground">
                Vragen over je bestelling? Neem contact op via{" "}
                <a href="mailto:info@lunssence.com" className="text-gold hover:underline">
                  info@lunssence.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};
